import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { getUserProfileIsTest, getUserRoleIsOmbudsman } from 'AuthPackage/MainModule/selectors/authSelectors';
import { makeGetCatalogDecisionConditionsSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import {
  getDictionariesApplicationConditionSubjectsData,
  getDictionariesEventHasUnplannedActivityData,
  getDictionariesEventUuidData,
  getDictionariesKnoSupervisionData,
  getDictionariesKnoSupervisionHazardClass,
  getDictionariesKnoSupervisionUuid,
  makeGetDictionariesDangerClassesSelectOptions,
  makeGetDictionariesDecisionKindsSelectOptions,
  makeGetDictionariesEventTypesSelectOptions,
  makeGetDictionariesParticipantPositionsSelectOptions,
  makeGetDictionariesPositionsSelectOptions,
  makeGetDictionariesRiskCategoriesSelectOptions,
  makeGetDictionariesRiskIndicatorsSelectOptionsForDecision,
  makeGetDictionariesSubjectKindsSelectOptions,
  makeGetDictionariesSubjectSubKindsSelectOptions,
  makeGetDictionariesSubjectTypesSelectOptions,
  makeGetDictionariesSupervisionTypesExtendedSelectOptions,
  makeGetDictionariesTargetEntitySelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { NEW_KNM_ID } from 'CorePackage/MainModule/routes/routes';
import { KnmModalsEnum } from 'KnmPackage/MainModule/enums/KnmModalsEnum';
import { KnmStatusesEnum } from 'KnmPackage/MainModule/enums/KnmStatusesEnum';
import { FEDERAL_LOW_248_GUID } from 'KnmPackage/MainModule/constants/erknmFederalLawUuid';

import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';

export const getDecisionLoading = (state: IRootState) => state.decision.decisionLoading;

export const getDecision = (state: IRootState) => state.decision;
export const getDecisionModalParams = (state: IRootState) => state.decision?.modalParams;
export const getDecisionActionsDropdownVisible = (state: IRootState) => getDecision(state).actionsDropdownVisible;
export const getDecisionErrorFields = (state: IRootState) => getDecision(state).errorFields;

export const getDecisionOnSaveButtonClick = (state: IRootState) => getDecision(state).onSaveButtonClick;

export const getDecisionData = (state: IRootState) => getDecision(state).rawData;
export const getDecisionDataAgreementOnApproval = (state: IRootState) => getDecisionData(state)?.agreementOnApproval;
export const getDecisionDataId = (state: IRootState) => getDecisionData(state)?.id;
export const getDecisionDataResigningRequired = (state: IRootState) => !!getDecisionData(state)?.resigningRequired;
export const getDecisionDataDecisionId = (state: IRootState) => getDecisionData(state)?.number;
export const getDecisionDataCreatedBy = (state: IRootState) => getDecisionData(state)?.createdBy;
export const getDecisionDataUpdatedBy = (state: IRootState) => getDecisionData(state)?.updatedBy;
export const getDecisionDataCreatedAt = (state: IRootState) => getDecisionData(state)?.createdAt;
export const getDecisionDataUpdatedAt = (state: IRootState) => getDecisionData(state)?.updatedAt;
export const getDecisionDataDeleted = (state: IRootState) => getDecisionData(state)?.deleted;
export const getDecisionDataAccessToken = (state: IRootState) => getDecisionData(state)?.accessToken;
export const getDecisionDataSigned = (state: IRootState) => getDecisionData(state)?.signed;
export const getDecisionDataSignatureInfo = (state: IRootState) => getDecisionData(state)?.signatureInfo;
export const getDecisionDataIsTest = (state: IRootState) => getDecisionData(state)?.isTest;
export const getDecisionModalIsVisible = (modalNames: KnmModalsEnum[]) => (state: IRootState) =>
  modalNames.some(modalName => state.decision?.decisionVisibleModals?.includes(modalName));
export const getDecisionPublishedStatus = (state: IRootState) => getDecisionData(state)?.publishedStatus;
export const getDecisionMassCancel = (state: IRootState) => getDecisionData(state)?.massCancel;
export const getDecisionMassDelete = (state: IRootState) => getDecisionData(state)?.massDelete;
export const getDecisionMassCancelBlock = (state: IRootState) => !!getDecisionData(state)?.massCancel?.isBlockEditing;

export const getDecisionHistoryChangesIsOn = (state: IRootState) => !!getDecision(state).decisionHistoryChangesIsOn;
export const getDecisionHistoryChanges = (state: IRootState) => getDecision(state).decisionHistoryChanges;
export const getDecisionHistoryChangesIsLoading = (state: IRootState) =>
  !!getDecision(state).decisionHistoryChangesIsLoading;

export const getDecisionSignedHistoryChanges = (state: IRootState) => getDecision(state).decisionSignedHistoryChanges;

export const getDecisionFormRef = (state: IRootState) => getDecision(state).formRef;

export const getDecisionFormAgreementsValue = (state: IRootState) => {
  const formRef = getDecisionFormRef(state);

  return formRef?.current?.getFieldState?.('agreements')?.value;
};

export const getDecisionInitialFormValues = (state: IRootState) => getDecision(state).initialFormValues;

export const getDecisionUrlId = (state: IRootState) => getDecision(state).urlId;
export const getDecisionIsNew = (state: IRootState) => getDecisionUrlId(state) === NEW_KNM_ID;

export const getDecisionUpdate = (state: IRootState) => getDecision(state).updateDecision;
export const getDecisionUpdateStatus = (state: IRootState) => getDecisionUpdate(state)?.status;
export const getDecisionUpdateIsSuccess = (state: IRootState) => {
  const status: KnmStatusesEnum | undefined = getDecisionUpdateStatus(state);

  return status === KnmStatusesEnum.SUCCEEDED;
};

export const getDecisionWatchOnlyByTestUser = (state: IRootState) => {
  const isUserTest: boolean = getUserProfileIsTest(state);
  const isTest = getDecisionDataIsTest(state);
  const isNewDecision: boolean = getDecisionIsNew(state) || false;

  if (isNewDecision) return false;

  return isUserTest ? !isTest : false;
};

export const getDecisionIsBasicWatchOnly = (state: IRootState) => {
  const isOmbudsman: boolean | undefined = getUserRoleIsOmbudsman(state);
  const isDeleted: boolean | undefined = getDecisionDataDeleted(state);
  const isWatchOnlyByTestUser = getDecisionWatchOnlyByTestUser(state);
  const isMassCancelBlock = getDecisionMassCancelBlock(state);

  return isDeleted || isOmbudsman || isWatchOnlyByTestUser || isMassCancelBlock;
};
export const getDecisionIsCommonWatchOnly = (state: IRootState) => getDecisionIsBasicWatchOnly(state);

export const getDecisionStatus = (state: IRootState) => getDecision(state).status;

export const getDecisionKnoOrganizationFormValue = (state: IRootState) => state.decision.formValues?.knoOrganization;
export const getDecisionSupervisionTypeFormValue = (state: IRootState) => state.decision.formValues?.supervisionType;
export const getDecisionKindDictVersionIdFormValue = (state: IRootState) => state.decision?.formValues?.kindVersionId;
export const getDecisionReasonsFormValue = (state: IRootState) => state.decision?.formValues?.reasons;

export const getDecisionSupervisionTypesSelectOptions = (state: IRootState) => {
  const getDictionariesSupervisionTypesSelectOptions = makeGetDictionariesSupervisionTypesExtendedSelectOptions();
  const knoOrganization = getDecisionKnoOrganizationFormValue(state);

  const settings = getAdminFetchSettings(state);

  const isUseTransferringAuthorities = isLaterThanAdminSetting(
    getDecisionDataCreatedAt(state),
    settings,
    AdminSettingCodesEnum.TRANSFERRING_AUTHORITIES_4_1_22_STARTS_FROM,
  );

  if (knoOrganization?.value.dictVersionId) {
    return getDictionariesSupervisionTypesSelectOptions(
      state,
      [knoOrganization?.value?.dictVersionId],
      isUseTransferringAuthorities,
      false,
      [FEDERAL_LOW_248_GUID],
    );
  }

  return undefined;
};

export const getDecisionDecisionKindsSelectOptions = (state: IRootState) => {
  const getDictionariesDecisionKindsSelectOptions = makeGetDictionariesDecisionKindsSelectOptions();
  const knoOrganization = getDecisionKnoOrganizationFormValue(state);
  const supervisionType = getDecisionSupervisionTypeFormValue(state);

  if (knoOrganization?.value?.dictVersionId && supervisionType?.value?.dictVersionId) {
    return getDictionariesDecisionKindsSelectOptions(state, {
      knoVersionId: knoOrganization?.value?.dictVersionId,
      supervisionVersionId: supervisionType?.value?.dictVersionId,
    });
  }

  return undefined;
};

export const getCurrentDecisionKindCode = (state: IRootState) => {
  const getDictionariesDecisionKindsSelectOptions = makeGetDictionariesDecisionKindsSelectOptions();
  const knoOrganization = getDecisionKnoOrganizationFormValue(state);
  const supervisionType = getDecisionSupervisionTypeFormValue(state);
  const currentDecisionKindId = getDecisionKindDictVersionIdFormValue(state);

  if (knoOrganization?.value?.dictVersionId && supervisionType?.value?.dictVersionId && currentDecisionKindId) {
    return getDictionariesDecisionKindsSelectOptions(state, {
      knoVersionId: knoOrganization?.value?.dictVersionId,
      supervisionVersionId: supervisionType?.value?.dictVersionId,
    })?.find(kind => kind.value.dictVersionId === currentDecisionKindId)?.code;
  }
};

export const getDecisionTargetEntitySelectOptions = (state: IRootState) => {
  const getDictionariesTargetEntitySelectOptions = makeGetDictionariesTargetEntitySelectOptions();
  const knoVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionVersionId = getDecisionSupervisionTypeFormValue(state)?.value?.dictVersionId;
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesTargetEntitySelectOptions(state, {
      knoVersionId,
      supervisionVersionId,
      conditionUuid: reasonUuid,
      orderVersionId: orderVersionUuid,
    });
  }

  return undefined;
};

export const getDecisionKnoSupervisionUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionTypeDictVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesKnoSupervisionUuid(state, knoOrganizationDictVersionId, supervisionTypeDictVersionId);
  }

  return undefined;
};

export const getDecisionEventUuid = (state: IRootState) => {
  const knoOrganizationDictVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionTypeDictVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;
  const knmKindVersionId = getDecisionKindDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId && knmKindVersionId) {
    return getDictionariesEventUuidData(
      state,
      knoOrganizationDictVersionId,
      supervisionTypeDictVersionId,
      knmKindVersionId,
    );
  }

  return undefined;
};

export const getDecisionEventHasUnplannedActivity = (state: IRootState) => {
  const knoOrganizationDictVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionTypeDictVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;
  const knmKindVersionId = getDecisionKindDictVersionIdFormValue(state);

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId && knmKindVersionId) {
    return getDictionariesEventHasUnplannedActivityData(
      state,
      knoOrganizationDictVersionId,
      supervisionTypeDictVersionId,
      knmKindVersionId,
    );
  }

  return undefined;
};

export const getDecisionRiskCategoriesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoSupervisionUuid && (reasonUuid || orderVersionUuid)) {
    return makeGetDictionariesRiskCategoriesSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      orderVersionUuid,
      undefined,
      undefined,
    );
  }

  return undefined;
};
export const getDecisionDangerClassesSelectOptions = (state: IRootState) => {
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoSupervisionUuid && (reasonUuid || orderVersionUuid)) {
    return makeGetDictionariesDangerClassesSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      orderVersionUuid,
      undefined,
    );
  }

  return undefined;
};

export const getDecisionSubjectTypesSelectOptions = (state: IRootState) => {
  const getDictionariesSubjectTypesSelectOptions = makeGetDictionariesSubjectTypesSelectOptions();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoSupervisionUuid && (reasonUuid || orderVersionUuid)) {
    return getDictionariesSubjectTypesSelectOptions(state, knoSupervisionUuid, reasonUuid, orderVersionUuid);
  }

  return undefined;
};

export const getDecisionSubjectKindsSelectOptions = (state: IRootState, objectTypeDictVersionId?: string) => {
  const getDictionariesSubjectKindsSelectOptions = makeGetDictionariesSubjectKindsSelectOptions();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoSupervisionUuid && (reasonUuid || orderVersionUuid)) {
    return getDictionariesSubjectKindsSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      orderVersionUuid,
      objectTypeDictVersionId,
    );
  }

  return undefined;
};
export const getDecisionSubjectSubKindsSelectOptions = (state: IRootState, objectTypeDictVersionId?: string) => {
  const getDictionariesSubjectSubKindsSelectOptions = makeGetDictionariesSubjectSubKindsSelectOptions();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (knoSupervisionUuid && (reasonUuid || orderVersionUuid)) {
    return getDictionariesSubjectSubKindsSelectOptions(
      state,
      knoSupervisionUuid,
      reasonUuid,
      orderVersionUuid,
      objectTypeDictVersionId,
    );
  }

  return undefined;
};
export const getDecisionParticipantPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesParticipantPositionsSelectOptions = makeGetDictionariesParticipantPositionsSelectOptions();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesParticipantPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};

export const getDecisionPositionsSelectOptions = (state: IRootState) => {
  const getDictionariesPositionsSelectOptions = makeGetDictionariesPositionsSelectOptions();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesPositionsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};

export const getDecisionEventTypesSelectOptions = (state: IRootState) => {
  const getDictionariesEventTypesSelectOptions = makeGetDictionariesEventTypesSelectOptions();
  const eventUuid = getDecisionEventUuid(state)?.find(x => x);
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (eventUuid && (orderVersionUuid || reasonUuid)) {
    return getDictionariesEventTypesSelectOptions(state, eventUuid, reasonUuid, orderVersionUuid);
  }

  return undefined;
};

export const getDecisionStatusCode = (state: IRootState) => getDecision(state).status?.code;

export const getKnmIsDecisionStatusTypeCompleted = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_COMPLETED;
export const getKnmIsDecisionStatusTypeDisapproved = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_DISAPPROVED;
export const getKnmIsDecisionStatusFillInProcess = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.FILL_IN_PROCESS;
export const getKnmIsDecisionStatusReadyForApprove = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.READY_FOR_APPROVE;
export const getKnmIsDecisionStatusTypeCantBeCarryOut = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_CANT_BE_CARRY_OUT;
export const getKnmIsDecisionStatusTypeCanceled = (state: IRootState) =>
  getDecisionStatusCode(state) === KnmErknmStatusCodesEnum.TYPE_CANCELED;

export const getDecisionKnoSupervisionHazardClassApplies = (state: IRootState) => {
  const knoVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;

  if (knoVersionId && supervisionVersionId) {
    return getDictionariesKnoSupervisionHazardClass(state, knoVersionId, supervisionVersionId);
  }
};
export const getKnoSupervisionStartAvailableUnplannedActivities = (state: IRootState) => {
  const knoOrganizationDictVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionTypeDictVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;

  if (knoOrganizationDictVersionId && supervisionTypeDictVersionId) {
    return getDictionariesKnoSupervisionData(state, knoOrganizationDictVersionId, supervisionTypeDictVersionId)
      ?.availableUnplannedActivities;
  }
};

// Кнопка обновления доступна, если статус
export const getDecisionIsCanRefreshControlObject = (state: IRootState) => {
  // В процессе заполнения
  return getKnmIsDecisionStatusFillInProcess(state);
};

export const getIsDecisionCanBeSigned = (state: IRootState) => getDecision(state).isDecisionCanBeSigned;

export const getDecisionReasonTypesSelectOptions = (state: IRootState) => {
  const getDecisionReasonTypesExtendedSelectOptions = makeGetCatalogDecisionConditionsSelectOptions();
  const knoVersionId = getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId;
  const supervisionVersionId = getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId;
  const decisionKindId = getDecisionKindDictVersionIdFormValue(state);

  return getDecisionReasonTypesExtendedSelectOptions(state, knoVersionId, supervisionVersionId, decisionKindId);
};

export const getDecisionRiskIndicatorsSelectOptions = (state: IRootState) => {
  const getDictionariesRiskIndicatorsSelectOptions = makeGetDictionariesRiskIndicatorsSelectOptionsForDecision();
  const knoSupervisionUuid = getDecisionKnoSupervisionUuid(state);

  if (knoSupervisionUuid) {
    return getDictionariesRiskIndicatorsSelectOptions(state, knoSupervisionUuid);
  }

  return undefined;
};
export const getDecisionOrdersSelectOptions = (state: IRootState) => state.decision.decisionOrdersSelectOptions;
export const getDecisionAOrdersSelectConditionId = (state: IRootState) => state.decision.decisionOrderConditionId;

export const getDecisionApplicationConditionSubjectsOptions = (state: IRootState) => {
  const reasons = getDecisionReasonsFormValue(state);
  const reasonUuid = reasons?.map(x => x?.ervkUuid)?.find(uuid => uuid);
  const orderVersionUuid = reasons?.map(x => x?.order?.recordVersionId)?.find(uuid => uuid);

  if (reasonUuid || orderVersionUuid) {
    return getDictionariesApplicationConditionSubjectsData(state, reasonUuid, orderVersionUuid);
  }

  return undefined;
};

export const getDecisionStatusHintText = (state: IRootState) => state.decision.statusHintText;
export const getDecisionStatusHintFields = (state: IRootState) => state.decision.statusHintFields;
export const getDecisionStatusHasHintFields = (state: IRootState) => Boolean(state.decision.statusHintFields?.length);
export const getErknmDecisionPrescribeIsCancelSignCompleted = (state: IRootState) =>
  getDecision(state).isCancelSignCompleted;

export const getDecisionExternalParams = (state: IRootState) => state.decision.externalParams;

export const getDecisionIsPrescriptionOldRules = (state: IRootState): boolean | undefined =>
  state.decision.isPrescriptionCheckByOldRules;

export const getDecisionIsPrescriptionFailed = (state: IRootState): boolean | undefined =>
  state.decision.isPassportByCheckPrescriptionFailed;
